import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import TwitterIcon from '@mui/icons-material/Twitter';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { Box, Container, Grid, IconButton, Stack } from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import companylogo from '../images/companylogo.jpeg';
import './Footer.css';

const services = [
  { label: 'Platinum Stock Future', to: '/services/platinum-stock-future' },
  { label: 'Classic Stock Future', to: '/services/classic-stock-future' },
  { label: 'Platinum Stock Option', to: '/services/platinum-stock-option' },
  { label: 'Classic Stock Option', to: '/services/classic-stock-option' },
  { label: 'Platinum Index Future', to: '/services/platinum-index-future' },
  { label: 'Platinum Index Options', to: '/services/platinum-index-options' },
  { label: 'Classic Index Options', to: '/services/classic-index-options' },
  { label: 'Platinum Equity', to: '/services/platinum-equity' },
  { label: 'Classic Equity', to: '/services/classic-equity' },
  { label: 'BTST / Short Term / Long Term', to: '/services/btst-short-term-long-term' },
];

const quickLinks = [
  { label: 'Home', to: '/' },
  { label: 'About Us', to: '/about' },
  { label: 'Equity', to: '/equity' },
  { label: 'Derivatives', to: '/derivative' },
  { label: 'Tracksheet', to: '/track-sheet' },
  { label: 'Complaint Status', to: '/complaint-status' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Disclosures', to: '/disclosures-advise' },
  { label: 'Bank Details', to: '/bank-details' },
  { label: 'Contact', to: '/contact' },
];

const socials = [
  { Icon: FacebookIcon, href: 'https://facebook.com', label: 'Facebook', color: '#1877f2' },
  { Icon: TwitterIcon, href: 'https://twitter.com', label: 'Twitter', color: '#1da1f2' },
  { Icon: InstagramIcon, href: 'https://instagram.com', label: 'Instagram', color: '#e4405f' },
  { Icon: LinkedInIcon, href: 'https://linkedin.com', label: 'LinkedIn', color: '#0a66c2' },
  { Icon: YouTubeIcon, href: 'https://youtube.com', label: 'YouTube', color: '#ff0000' },
  { Icon: WhatsAppIcon, href: 'https://wa.me/919510980382', label: 'WhatsApp', color: '#25d366' },
];

const ACCENT = '#ef6a1d';

const FooterLink = ({ to, children }) => (
  <Box
    component={Link}
    to={to}
    className="rs-footer-link"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      color: '#cbd5e1',
      textDecoration: 'none',
      fontSize: '0.92rem',
      lineHeight: 1.9,
      transition: 'all 0.2s ease',
      '& svg': {
        fontSize: '0.95rem',
        opacity: 0,
        transform: 'translateX(-4px)',
        transition: 'all 0.2s ease',
      },
      '&:hover': {
        color: ACCENT,
        transform: 'translateX(2px)',
        '& svg': { opacity: 1, transform: 'translateX(0)' },
      },
    }}
  >
    <ChevronRightIcon />
    {children}
  </Box>
);

const SectionTitle = ({ children }) => (
  <Box
    component="h3"
    sx={{
      fontSize: '1.05rem',
      fontWeight: 700,
      color: '#fff',
      mb: 2.5,
      pb: 1.2,
      position: 'relative',
      letterSpacing: 0.3,
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        bottom: 0,
        width: 36,
        height: 3,
        background: ACCENT,
        borderRadius: 2,
      },
    }}
  >
    {children}
  </Box>
);

const Footer = () => {
  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        background:
          'linear-gradient(160deg, #0b2447 0%, #07203f 55%, #061a36 100%)',
        color: '#cbd5e1',
        pt: { xs: 6, md: 8 },
        pb: 3,
        mt: 'auto',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 50%, ${ACCENT} 100%)`,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -120,
          right: -120,
          width: 360,
          height: 360,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(239,106,29,0.15) 0%, rgba(239,106,29,0) 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4}>
          {/* Brand */}
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src={companylogo}
              alt="Real Stock Experts"
              sx={{
                height: 56,
                background: '#fff',
                p: 1,
                borderRadius: 1.5,
                mb: 2.5,
              }}
            />
            <Box
              sx={{
                fontSize: '0.92rem',
                lineHeight: 1.75,
                color: '#94a3b8',
                mb: 2.5,
                maxWidth: 380,
              }}
            >
              We are SEBI registered as Research Analyst, providing trusted
              research recommendations to traders and investors in the Indian
              equity market.
            </Box>

            <Box
              sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.6,
                mb: 2.5,
                borderRadius: 1,
                border: '1px solid rgba(239,106,29,0.35)',
                background: 'rgba(239,106,29,0.08)',
                color: ACCENT,
                fontSize: '0.8rem',
                fontWeight: 600,
                letterSpacing: 0.4,
              }}
            >
              SEBI REGISTERED · INVESTOR CHARTER RA
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {socials.map(({ Icon, href, label, color }) => (
                <IconButton
                  key={label}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  sx={{
                    color: '#fff',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    width: 38,
                    height: 38,
                    transition: 'all 0.25s ease',
                    '& svg': { fontSize: '1.1rem' },
                    '&:hover': {
                      background: color,
                      borderColor: color,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 8px 18px ${color}55`,
                    },
                  }}
                >
                  <Icon />
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={3}>
            <SectionTitle>Our Services</SectionTitle>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                rowGap: 0.2,
              }}
            >
              {services.map((s) => (
                <FooterLink key={s.to + s.label} to={s.to}>
                  {s.label}
                </FooterLink>
              ))}
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <SectionTitle>Quick Links</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {quickLinks.map((l) => (
                <FooterLink key={l.to} to={l.to}>
                  {l.label}
                </FooterLink>
              ))}
            </Box>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} md={3}>
            <SectionTitle>Get In Touch</SectionTitle>
            <Stack spacing={1.8}>
              <ContactRow
                Icon={PhoneIcon}
                label="Phone"
                value="+91 95109 80382"
                href="tel:+919510980382"
              />
              <ContactRow
                Icon={EmailIcon}
                label="Email"
                value="admin@realstockexperts.in"
                href="mailto:admin@realstockexperts.in"
              />
              <ContactRow
                Icon={AccessTimeIcon}
                label="Office Hours"
                value="Mon – Sat · 9:00 AM – 6:00 PM"
              />
              <ContactRow
                Icon={LocationOnIcon}
                label="Address"
                value="Mumbai, Maharashtra, India"
              />
            </Stack>
          </Grid>
        </Grid>

        {/* Divider */}
        <Box
          sx={{
            mt: { xs: 5, md: 6 },
            pt: 2.5,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>
            © {new Date().getFullYear()} Real Stock Experts. All Rights
            Reserved.
          </Box>
          <Stack
            direction="row"
            spacing={2.5}
            divider={
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.25)',
                  alignSelf: 'center',
                }}
              />
            }
            sx={{ fontSize: '0.85rem' }}
          >
            <Box
              component={Link}
              to="/disclosures-advise"
              sx={{
                color: '#94a3b8',
                textDecoration: 'none',
                '&:hover': { color: ACCENT },
              }}
            >
              Disclosures
            </Box>
            <Box
              component={Link}
              to="/complaint-status"
              sx={{
                color: '#94a3b8',
                textDecoration: 'none',
                '&:hover': { color: ACCENT },
              }}
            >
              Complaint Status
            </Box>
            <Box
              component={Link}
              to="/faq"
              sx={{
                color: '#94a3b8',
                textDecoration: 'none',
                '&:hover': { color: ACCENT },
              }}
            >
              FAQ
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

const ContactRow = ({ Icon, label, value, href }) => {
  const valueEl = (
    <Box
      sx={{
        fontSize: '0.92rem',
        color: '#e2e8f0',
        fontWeight: 500,
        lineHeight: 1.4,
        textDecoration: 'none',
        transition: 'color 0.2s ease',
        '&:hover': href ? { color: ACCENT } : undefined,
      }}
      component={href ? 'a' : 'div'}
      href={href}
    >
      {value}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 38,
          height: 38,
          borderRadius: 1.5,
          background: 'rgba(239,106,29,0.12)',
          color: ACCENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(239,106,29,0.2)',
        }}
      >
        <Icon sx={{ fontSize: '1.1rem' }} />
      </Box>
      <Box>
        <Box
          sx={{
            fontSize: '0.72rem',
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
            mb: 0.2,
          }}
        >
          {label}
        </Box>
        {valueEl}
      </Box>
    </Box>
  );
};

export default Footer;
